import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Baby, User, Users, ShoppingBag } from 'lucide-react';

function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error('Failed to fetch categories');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-screen pb-20 md:pb-8 py-6 px-4">
      <Helmet>
        <title>All Categories | Mothering</title>
        <meta name="description" content="Browse all product categories - Education, Toys, Tiffin, Bags, Bottles, and more" />
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold mb-2">All Categories</h1>
          <p className="text-muted-foreground">Browse by category to find what you need</p>
        </div>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Shop by Age</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/shop/age/0-2">
              <Card className="hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-pink-100 flex items-center justify-center">
                    <Baby className="w-8 h-8 text-pink-600" />
                  </div>
                  <h3 className="font-semibold mb-1">0-2 Years</h3>
                  <p className="text-sm text-muted-foreground">Newborn & Toddler</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/shop/age/3-5">
              <Card className="hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-cyan-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="font-semibold mb-1">3-5 Years</h3>
                  <p className="text-sm text-muted-foreground">Preschool</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/shop/age/6-12">
              <Card className="hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-orange-100 flex items-center justify-center">
                    <Users className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold mb-1">6-12 Years</h3>
                  <p className="text-sm text-muted-foreground">School Age</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">Product Categories</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.map((category) => (
              <Link 
                key={category.id} 
                to={`/products?category=${category.id}`}
                className="group"
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-cyan-100">
                  <CardContent className="p-4 text-center space-y-3">
                    <div className="w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-cyan-100 to-orange-50 overflow-hidden group-hover:scale-110 transition-transform">
                      {category.image_url ? (
                        <img 
                          src={category.image_url} 
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-10 h-10 text-cyan-500" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold text-sm group-hover:text-cyan-600 transition-colors">
                      {category.name}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default CategoriesPage;
